/**
 * `/ship` — the shipping station (P5 S3), built to `docs/reference/ship-station-mockup.html`.
 *
 * A full-bleed, forced-dark kiosk on its own bench device (the second Rollo is that machine's default
 * printer), in the same posture as `/scan`: no app shell, page-level scanner capture, wake lock.
 *
 * Three screens, one page:
 *   QUEUE  — every ship-ready order, held ones included and blocked (see `ship-lib`: the queue reads
 *            BOTH the `printed` and `readyToShip` lanes; `readyToShip` alone is caps + Ship-only).
 *   PACK   — the expected box contents with live verification (scan a tag, or log an eye-check for a
 *            tagless reissue), beside the parcel/rate rail and the buy gate.
 *   DONE   — the bought label: tracking, 4×6 reprint, void, and the Shopify write-back.
 *
 * ⚠️ MONEY PATH. `POST …/buy` spends real postage. Everything here is a MIRROR of the server's gate,
 * never the gate itself: the endpoint re-checks verification (strongly consistent), the reprint hold,
 * the order's fulfilment state and the one-live-label rule on its own authority, and this page shows
 * whatever it refuses. Two client-side protections that are genuinely load-bearing:
 *   • the buy button is guarded by a REF, not `disabled={!isPending}` — a double-click resolves before
 *     React re-renders, and the second click would be a second label (the documented race);
 *   • an unresolved purchase (`in_flight`/`unknown`) blocks the whole panel — retrying THAT shipment
 *     is the only safe move, and quoting a fresh one beside it is how an order gets two labels.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Eye,
  ImageOff,
  Keyboard,
  Loader2,
  Package,
  Printer,
  RefreshCw,
  Truck,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWakeLock } from '@/hooks/use-wake-lock';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import { useScannerCapture } from './scan-capture';
import { ShipLabelPrint } from './ship-label-print';
import {
  ageLabel,
  BOX_PRESETS,
  boughtShipment,
  buyGate,
  estimateParcel,
  formatUsd,
  formatWeight,
  fulfillmentState,
  isRatableParcel,
  isTestLabel,
  linePips,
  queueRowChip,
  queueRowSummary,
  quotedShipment,
  rateEta,
  ratesCheapestFirst,
  type ShipManifest,
  type ShipManifestLine,
  type Shipment,
  type ShipmentRate,
  type ShipParcel,
  type ShipQueueRow,
  type ShipScanView,
  type ShipVerifyProgress,
  type ShipVerifyRequest,
  sameParcel,
  shipQueue,
  shipScanView,
  shipStage,
  totalOwed,
  totalVerified,
  unresolvedShipment,
} from './ship-lib';

type Selected = { storeId: string; orderId: string };

const TONE_BAND: Record<ShipScanView['tone'], string> = {
  success: 'border-emerald-800 bg-emerald-950/40 text-emerald-100',
  warn: 'border-amber-800 bg-amber-950/40 text-amber-100',
  error: 'border-red-800 bg-red-950/50 text-red-100',
  info: 'border-border bg-muted/30 text-foreground',
};

/** Best-effort scan feedback. Silent when WebAudio is unavailable — the scan itself is the gesture. */
let audioCtx: AudioContext | undefined;
function beep(kind: 'ok' | 'bad'): void {
  try {
    audioCtx ??= new (
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'square';
    osc.frequency.value = kind === 'ok' ? 880 : 200;
    gain.gain.setValueAtTime(0.06, audioCtx.currentTime);
    osc.start();
    osc.stop(audioCtx.currentTime + (kind === 'ok' ? 0.1 : 0.3));
  } catch {
    // audio unavailable — silent
  }
}

export function ShipStationPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  useWakeLock();

  const [selected, setSelected] = useState<Selected | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const manualRef = useRef<HTMLInputElement>(null);

  const boardQuery = useQuery({
    queryKey: ['production-board'],
    queryFn: async () => {
      const { data, error } = await client.GET('/production/board');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the queue.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const manifestQuery = useQuery({
    queryKey: ['ship-manifest', selected?.storeId, selected?.orderId],
    enabled: selected !== null,
    queryFn: async () => {
      if (!selected) throw new Error('No order selected.');
      const { data, error } = await client.GET('/shipments/{storeId}/{orderId}/manifest', {
        params: { path: { storeId: selected.storeId, orderId: selected.orderId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the pack manifest.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const manifest = manifestQuery.data ?? null;

  /** Refresh everything a ship action moves: this order's manifest, the queue, and the job card. */
  function refreshOrder(storeId: string, orderId: string) {
    qc.invalidateQueries({ queryKey: ['ship-manifest', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['production-board'] });
    qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
  }

  return (
    <div className="dark flex h-dvh w-full flex-col overflow-hidden bg-background text-foreground">
      <TopBar
        scanning={selected !== null && !manualOpen}
        onManual={() => setManualOpen(true)}
        onRefresh={() => {
          if (selected) refreshOrder(selected.storeId, selected.orderId);
          else qc.invalidateQueries({ queryKey: ['production-board'] });
        }}
        refreshing={boardQuery.isFetching || manifestQuery.isFetching}
      />

      <main className="min-h-0 flex-1 overflow-auto p-4">
        {selected === null ? (
          <QueueView
            rows={boardQuery.data ? shipQueue(boardQuery.data) : []}
            loading={boardQuery.isLoading}
            error={boardQuery.error instanceof Error ? boardQuery.error.message : null}
            onPack={(row) =>
              setSelected({ storeId: row.order.storeId, orderId: row.order.orderId })
            }
          />
        ) : manifestQuery.isLoading ? (
          <Centered>
            <Loader2 className="size-6 animate-spin" /> Loading the box…
          </Centered>
        ) : manifestQuery.error instanceof Error ? (
          <Centered tone="error">{manifestQuery.error.message}</Centered>
        ) : manifest ? (
          <OrderView
            manifest={manifest}
            manualOpen={manualOpen}
            setManualOpen={setManualOpen}
            manualRef={manualRef}
            onBack={() => setSelected(null)}
            refreshOrder={refreshOrder}
          />
        ) : null}
      </main>
    </div>
  );
}

function TopBar({
  scanning,
  onManual,
  onRefresh,
  refreshing,
}: {
  scanning: boolean;
  onManual: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <header className="flex flex-none items-center gap-3 border-b border-border px-4 py-3">
      <Truck className="size-5 text-muted-foreground" />
      <h1 className="text-lg font-semibold">Shipping</h1>
      <div className="ml-auto flex items-center gap-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'size-2 rounded-full',
              scanning ? 'animate-pulse bg-emerald-400' : 'bg-muted-foreground',
            )}
          />
          {scanning ? 'Scanner ready' : 'Scanner idle'}
        </span>
        <Button variant="outline" size="sm" onClick={onManual} disabled={!scanning}>
          <Keyboard className="size-4" /> Type a code
        </Button>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} /> Refresh
        </Button>
      </div>
    </header>
  );
}

function Centered({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      className={cn(
        'flex h-full items-center justify-center gap-2 text-sm',
        tone === 'error' ? 'text-red-300' : 'text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Queue
// ─────────────────────────────────────────────────────────────────────────────

function QueueView({
  rows,
  loading,
  error,
  onPack,
}: {
  rows: ShipQueueRow[];
  loading: boolean;
  error: string | null;
  onPack: (row: ShipQueueRow) => void;
}) {
  const now = Date.now();
  const ready = rows.filter((r) => r.state === 'ready').length;
  const held = rows.length - ready;

  if (loading) {
    return (
      <Centered>
        <Loader2 className="size-6 animate-spin" /> Loading the queue…
      </Centered>
    );
  }
  if (error) return <Centered tone="error">{error}</Centered>;

  return (
    <div className="mx-auto max-w-5xl">
      <p className="mb-3 text-sm text-muted-foreground">
        {ready} ready to ship{held > 0 ? ` · ${held} held` : ''}
      </p>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-border p-10 text-center text-sm text-muted-foreground">
          Nothing is waiting to ship. Printed and packed orders land here automatically.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => (
            <QueueRow
              key={`${row.order.storeId}#${row.order.orderId}`}
              row={row}
              now={now}
              onPack={() => onPack(row)}
            />
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Held orders stay visible — an order with an outstanding reprint can’t reach a label, the
        same rule the production board enforces. Rush first, then due date, then oldest.
      </p>
    </div>
  );
}

function QueueRow({ row, now, onPack }: { row: ShipQueueRow; now: number; onPack: () => void }) {
  const o = row.order;
  const held = row.state === 'held';
  return (
    <div className="grid grid-cols-[minmax(0,10rem)_3rem_minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3 max-[820px]:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="truncate font-semibold">{o.orderName}</div>
        <div className="truncate text-xs text-muted-foreground">{o.brandLabel ?? o.storeId}</div>
      </div>
      <div className="text-sm tabular-nums text-muted-foreground max-[820px]:hidden">
        {ageLabel(o.shopifyCreatedAt, now)}
      </div>
      <div className="min-w-0 truncate text-sm max-[820px]:hidden">
        <span className="font-medium">{queueRowSummary(o)}</span>
        {held && row.heldReason ? <span className="text-red-300"> · {row.heldReason}</span> : null}
      </div>
      <span
        className={cn(
          'rounded-full border px-2 py-0.5 text-xs max-[820px]:hidden',
          held
            ? 'border-red-800 bg-red-950/40 text-red-200'
            : 'border-border text-muted-foreground',
        )}
      >
        {held ? 'Held · reprint' : queueRowChip(o)}
      </span>
      <Button size="sm" onClick={onPack} disabled={held} variant={held ? 'outline' : 'default'}>
        {held ? 'Held' : 'Pack'}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Order (pack + done)
// ─────────────────────────────────────────────────────────────────────────────

function OrderView({
  manifest,
  manualOpen,
  setManualOpen,
  manualRef,
  onBack,
  refreshOrder,
}: {
  manifest: ShipManifest;
  manualOpen: boolean;
  setManualOpen: (v: boolean) => void;
  manualRef: React.RefObject<HTMLInputElement | null>;
  onBack: () => void;
  refreshOrder: (storeId: string, orderId: string) => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { storeId, orderId } = manifest;
  const stage = shipStage(manifest);
  // `shipStage` is 'done' exactly when this is non-null — deriving it here lets DoneView take a
  // plain `Shipment` instead of a nullable one.
  const bought = boughtShipment(manifest);

  const [scanView, setScanView] = useState<ShipScanView | null>(null);
  const [eyeFor, setEyeFor] = useState<ShipManifestLine | null>(null);

  /** Apply a verify response: the server returns the fresh per-line tally, so patch the cache. */
  function applyVerify(lines: ShipVerifyProgress[] | undefined) {
    if (!lines) return;
    qc.setQueryData(['ship-manifest', storeId, orderId], (prev: ShipManifest | undefined) =>
      prev
        ? {
            ...prev,
            lines: prev.lines.map((l) => {
              const fresh = lines.find((f) => f.lineId === l.lineId);
              return fresh ? { ...l, ...fresh } : l;
            }),
            allVerified: lines.every((l) => l.complete),
          }
        : prev,
    );
  }

  const verifyMut = useMutation({
    mutationFn: async (body: ShipVerifyRequest) => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/verify', {
        params: { path: { storeId, orderId } },
        body,
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not record that scan.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: (data) => {
      applyVerify(data.lines);
      // Deliberately no `scannedOrderName`: the verify response carries only IDS, and printing a raw
      // Shopify order id at the loudest moment in the station gives the packer nothing to match to a
      // pile. The generic copy tells them what to do instead.
      const view = shipScanView(data.status);
      setScanView(view);
      beep(view.tone === 'success' ? 'ok' : 'bad');
    },
    onError: (e: unknown) => {
      setScanView({
        tone: 'error',
        title: 'Scan failed',
        detail: e instanceof Error ? e.message : 'Could not reach the server.',
        loud: false,
      });
      beep('bad');
    },
  });

  useScannerCapture({
    onScan: (code) => {
      if (stage !== 'pack') return;
      const trimmed = code.trim();
      if (trimmed) verifyMut.mutate({ code: trimmed });
    },
    enabled: stage === 'pack' && !manualOpen && eyeFor === null,
  });

  const resetMut = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/verify/reset', {
        params: { path: { storeId, orderId } },
      });
      if (res.error)
        throw new Error(res.error?.error?.detail ?? 'Could not clear the verification.');
      return res.data?.data;
    },
    onSuccess: () => {
      setScanView(null);
      refreshOrder(storeId, orderId);
    },
    // The server refuses this with 422 `label-live` once a label is bought or a buy is in flight.
    // Without this the operator taps Clear, nothing happens, and they tap again.
    onError: (e: unknown) =>
      setScanView({
        tone: 'error',
        title: 'Could not clear',
        detail: e instanceof Error ? e.message : 'The server refused it.',
        loud: false,
      }),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="size-4" /> Queue
        </Button>
        <h2 className="text-xl font-semibold">{manifest.orderName}</h2>
        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
          {manifest.storeId}
        </span>
        <span className="text-sm text-muted-foreground">
          {totalOwed(manifest.lines)} garments · {manifest.lines.length} lines
        </span>
      </header>

      {bought ? (
        <DoneView
          manifest={manifest}
          shipment={bought}
          onBack={onBack}
          refreshOrder={refreshOrder}
        />
      ) : (
        <>
          {scanView?.loud ? (
            <div className={cn('mb-3 rounded-lg border p-4', TONE_BAND[scanView.tone])}>
              <div className="flex items-center gap-2 text-lg font-semibold">
                <AlertTriangle className="size-5" /> {scanView.title}
              </div>
              <p className="mt-1 text-sm">{scanView.detail}</p>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div>
              <div className="divide-y divide-border rounded-lg border border-border">
                {manifest.lines.map((line) => (
                  <LineRow key={line.lineId} line={line} onEyeCheck={() => setEyeFor(line)} />
                ))}
              </div>
              {scanView && !scanView.loud ? (
                <div
                  className={cn(
                    'mt-3 rounded-lg border px-3 py-2 text-sm',
                    TONE_BAND[scanView.tone],
                  )}
                >
                  <b>{scanView.title}</b> — {scanView.detail}
                </div>
              ) : null}
              <div className="mt-3 flex items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  Scan any garment tag to count it into the box. A tag can’t be counted past what
                  the line owes, and a tag from another order goes loud. Tagless garments (a reissue
                  prints no tag) take a logged eye-check.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto flex-none"
                  onClick={() => resetMut.mutate()}
                  disabled={resetMut.isPending}
                >
                  Clear
                </Button>
              </div>
            </div>

            <ShipRail manifest={manifest} refreshOrder={refreshOrder} />
          </div>
        </>
      )}

      {manualOpen ? (
        <ManualCodeDialog
          inputRef={manualRef}
          onClose={() => setManualOpen(false)}
          onSubmit={(code) => {
            setManualOpen(false);
            if (code) verifyMut.mutate({ code });
          }}
        />
      ) : null}

      {eyeFor ? (
        <EyeCheckDialog
          line={eyeFor}
          onClose={() => setEyeFor(null)}
          onConfirm={(count, reason) => {
            setEyeFor(null);
            verifyMut.mutate({ eyeCheck: { lineId: eyeFor.lineId, count, reason } });
          }}
        />
      ) : null}
    </div>
  );
}

function LineRow({ line, onEyeCheck }: { line: ShipManifestLine; onEyeCheck: () => void }) {
  const pips = linePips(line);
  return (
    <div className="flex items-center gap-4 p-3">
      <div className="size-16 flex-none overflow-hidden rounded border border-border bg-muted/30">
        {line.imageUrl ? (
          <img src={line.imageUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageOff className="size-5" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{line.title}</div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {line.sizeName ? (
            <span className="rounded border border-border px-1.5 py-0.5 font-semibold text-foreground">
              {line.sizeName}
            </span>
          ) : null}
          {line.variantTitle ? <span className="truncate">{line.variantTitle}</span> : null}
          {line.productionClass === 'FULFILLMENT_ONLY' ? (
            <span className="rounded border border-border px-1.5 py-0.5">Ship-only</span>
          ) : null}
          {/* Shelf provenance (bin-labels plan PR-3): these garments came off the finished-garment
              shelf, so no hem tag scans green for THIS order — the eye-check is their path, and this
              names the bin to check them against. Derived server-side from the FROZEN pick key. */}
          {line.stockPickBinId ? (
            <span className="rounded border border-amber-700 px-1.5 py-0.5 text-amber-300">
              garment shelf · {line.stockPickQty ?? '?'} · bin{' '}
              <span className="font-mono">{line.stockPickBinId}</span>
            </span>
          ) : null}
        </div>
        {line.eyeReason ? (
          <div className="mt-1 text-xs text-amber-300">👁 {line.eyeReason}</div>
        ) : null}
      </div>
      <div className="flex flex-none items-center gap-3">
        <div className="text-right">
          <div className="text-lg font-semibold tabular-nums">
            {Math.min(line.owed, line.verified)}
            <span className="text-sm text-muted-foreground"> of {line.owed}</span>
          </div>
          <div className="mt-1 flex justify-end gap-1">
            {pips.map((pip) => (
              <span
                key={`${line.lineId}#${pip.rank}`}
                title={`Garment ${pip.rank} of ${line.owed}`}
                className={cn(
                  'flex size-5 items-center justify-center rounded-full border text-[10px]',
                  pip.state === 'scanned'
                    ? 'border-emerald-700 bg-emerald-900/60 text-emerald-200'
                    : pip.state === 'eye'
                      ? 'border-amber-700 bg-amber-900/60 text-amber-200'
                      : 'border-border',
                )}
              >
                {pip.state === 'scanned' ? '✓' : pip.state === 'eye' ? '👁' : ''}
              </span>
            ))}
          </div>
        </div>
        {!line.complete ? (
          <Button variant="outline" size="sm" onClick={onEyeCheck} title="Log an eye-check">
            <Eye className="size-4" />
          </Button>
        ) : (
          <Check className="size-5 text-emerald-400" />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The rail: parcel → rates → gate → buy
// ─────────────────────────────────────────────────────────────────────────────

function ShipRail({
  manifest,
  refreshOrder,
}: {
  manifest: ShipManifest;
  refreshOrder: (storeId: string, orderId: string) => void;
}) {
  const client = useApiClient();
  const { storeId, orderId } = manifest;

  const [parcel, setParcel] = useState<ShipParcel>(() => estimateParcel(manifest));
  const [boxId, setBoxId] = useState<string>(BOX_PRESETS[0]?.id ?? '');
  const [chosenRateId, setChosenRateId] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The buy is guarded by a REF, not by `disabled` state: two fast clicks both read the old `disabled`
  // before React re-renders, and the second one would be a second label. The ref flips synchronously.
  const buyingRef = useRef(false);

  const quoted = quotedShipment(manifest);
  const unresolved = unresolvedShipment(manifest);
  const rates = quoted ? ratesCheapestFirst(quoted.rates) : [];
  const chosen = rates.find((r) => r.rateId === chosenRateId) ?? rates[0] ?? null;
  // A rate prices the parcel it was quoted for. If the operator has since changed weight or box, the
  // price on screen is for a different package — re-rate before it can be bought.
  const parcelChanged = quoted != null && !sameParcel(parcel, quoted.parcel);
  const ratable = isRatableParcel(parcel);

  const quoteMut = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/quote', {
        params: { path: { storeId, orderId } },
        body: { parcel },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not rate this parcel.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      setError(null);
      setChosenRateId(null);
      refreshOrder(storeId, orderId);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Rating failed.'),
  });

  const buyMut = useMutation({
    mutationFn: async (vars: { shipmentId: string; rateId: string; overrideReason?: string }) => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/{shipmentId}/buy', {
        params: { path: { storeId, orderId, shipmentId: vars.shipmentId } },
        body: {
          rateId: vars.rateId,
          ...(vars.overrideReason ? { override: { reason: vars.overrideReason } } : {}),
        },
      });
      if (res.error) {
        // No detail from the server means we never got an answer — say so. "Not bought" would be a
        // definite negative for an outcome that may well be a purchase.
        throw new Error(
          res.error?.error?.detail ??
            'The purchase did not return an answer — it may or may not have gone through. Reload before retrying.',
        );
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      setError(null);
      refreshOrder(storeId, orderId);
    },
    onError: (e: unknown) =>
      setError(
        e instanceof Error
          ? e.message
          : 'The purchase did not return an answer — reload before retrying.',
      ),
    onSettled: () => {
      buyingRef.current = false;
      // ALWAYS re-read, including on failure: the server may have pinned the attempt `unknown` (or
      // even recorded the label) while the response was lost. Without this the panel keeps a stale
      // manifest, the unresolved banner never appears, and nothing locks.
      refreshOrder(storeId, orderId);
    },
  });

  /**
   * Buy, or RECONCILE. When a purchase is unresolved the safe move is to re-POST buy against THAT
   * shipment: the server reconciles it against the pinned provider id first and adopts the label if
   * one exists (only resuming a fresh attempt when the provider proves nothing was bought). So the
   * unresolved case does not disable this button — it retargets it, and disables `Rate parcel`
   * instead, because a second quote is what actually risks a second label.
   */
  function buy(overrideReason?: string) {
    const target = unresolved ?? quoted;
    // On a reconcile, re-send the rate the ORIGINAL attempt pinned — a different one would change
    // what a resumed buy purchases.
    const rateId = unresolved ? (unresolved.chosenRateId ?? chosen?.rateId) : chosen?.rateId;
    if (buyingRef.current || !target || !rateId) return;
    buyingRef.current = true;
    buyMut.mutate({
      shipmentId: target.shipmentId,
      rateId,
      ...(overrideReason ? { overrideReason } : {}),
    });
  }

  // Computed after the mutations so it can read their in-flight state.
  const gate = buyGate(manifest, chosen, { parcelChanged, quoting: quoteMut.isPending });

  const shipTo = quoted?.shipTo;

  return (
    <aside className="flex flex-col gap-3">
      {unresolved ? (
        <div className="rounded-lg border border-amber-800 bg-amber-950/40 p-3 text-sm text-amber-100">
          <b>A purchase on this order is unresolved.</b>
          <p className="mt-1 text-xs">
            Shipment {unresolved.shipmentId.slice(0, 8)} is {unresolved.buyAttemptStatus}. Retry
            THAT shipment to settle it — never quote a new one beside it, or the order can end up
            with two labels.
          </p>
        </div>
      ) : null}

      <section className="rounded-lg border border-border p-3">
        <h3 className="mb-2 text-sm font-semibold">Ship to</h3>
        {shipTo ? (
          <div className="text-sm">
            <div className="font-medium">{shipTo.name}</div>
            <div className="text-muted-foreground">
              {shipTo.street1}
              {shipTo.street2 ? `, ${shipTo.street2}` : ''}, {shipTo.city} {shipTo.state}{' '}
              {shipTo.zip}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Fetched live from Shopify — kept only on this shipment.
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Fetched from Shopify when you rate the parcel — never stored on the order.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-border p-3">
        <h3 className="mb-2 text-sm font-semibold">Package</h3>
        <div className="flex items-center gap-2">
          <Label htmlFor="ship-weight" className="w-16 flex-none text-xs text-muted-foreground">
            Weight
          </Label>
          <Input
            id="ship-weight"
            name="weightOz"
            type="number"
            min={1}
            step={0.1}
            value={parcel.weightOz}
            onChange={(e) =>
              setParcel((p) => ({ ...p, weightOz: Math.max(0, Number(e.target.value)) }))
            }
            className="h-8"
          />
          <span className="w-20 flex-none text-right text-xs text-muted-foreground">
            {formatWeight(parcel.weightOz)}
          </span>
        </div>
        <p className="mt-1 text-right text-xs text-muted-foreground">
          estimate for {totalOwed(manifest.lines)} garments — edit if the scale disagrees
        </p>
        <div className="mt-2 flex items-center gap-2">
          <Label htmlFor="ship-box" className="w-16 flex-none text-xs text-muted-foreground">
            Box
          </Label>
          <select
            id="ship-box"
            name="box"
            className="h-8 flex-1 rounded-md border border-input bg-transparent px-2 text-sm"
            value={boxId}
            onChange={(e) => {
              const preset = BOX_PRESETS.find((b) => b.id === e.target.value);
              setBoxId(e.target.value);
              if (preset) {
                setParcel((p) => ({
                  ...p,
                  lengthIn: preset.lengthIn,
                  widthIn: preset.widthIn,
                  heightIn: preset.heightIn,
                }));
              }
            }}
          >
            {BOX_PRESETS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          className="mt-3 w-full"
          variant="outline"
          onClick={() => quoteMut.mutate()}
          disabled={
            quoteMut.isPending || !manifest.shipFromConfigured || !ratable || unresolved !== null
          }
          title={
            unresolved !== null
              ? 'Settle the unresolved purchase first — a second quote is how an order gets two labels.'
              : undefined
          }
        >
          {quoteMut.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" /> Rating…
            </>
          ) : (
            <>
              <Package className="size-4" /> {quoted ? 'Re-rate parcel' : 'Rate parcel'}
            </>
          )}
        </Button>
        {!manifest.shipFromConfigured ? (
          <p className="mt-2 text-xs text-red-300">
            Set the shop’s address in Settings › Purchasing › Ship-to address first — it is the
            label’s ship-from.
          </p>
        ) : !ratable ? (
          <p className="mt-2 text-xs text-amber-300">Enter a weight of at least 1 oz to rate.</p>
        ) : parcelChanged ? (
          <p className="mt-2 text-xs text-amber-300">
            The parcel changed since it was rated — re-rate before buying.
          </p>
        ) : null}
      </section>

      {rates.length > 0 ? (
        <section className="rounded-lg border border-border p-3">
          <h3 className="mb-2 text-sm font-semibold">Service</h3>
          <div className="flex flex-col gap-1" role="radiogroup" aria-label="Shipping service">
            {rates.map((rate, i) => (
              <RateRow
                key={rate.rateId}
                rate={rate}
                cheapest={i === 0}
                selected={chosen?.rateId === rate.rateId}
                onSelect={() => setChosenRateId(rate.rateId)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section
        className={cn(
          'rounded-lg border p-3 text-sm',
          gate.armed ? 'border-emerald-800 bg-emerald-950/30' : 'border-border bg-muted/20',
        )}
      >
        <div className="font-semibold">
          {gate.armed ? 'Armed — every garment verified' : 'Locked'}
        </div>
        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          <li>
            <b className="text-foreground">
              {totalVerified(manifest.lines)} of {totalOwed(manifest.lines)}
            </b>{' '}
            garments verified
          </li>
          {gate.blockers.map((b) => (
            <li key={b} className="text-amber-300">
              • {b}
            </li>
          ))}
          {gate.blockers.length === 0 ? <li className="text-emerald-300">• No blockers</li> : null}
        </ul>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-3 text-sm text-red-100">
          {error}
        </div>
      ) : null}

      <Button
        size="lg"
        className="w-full"
        disabled={buyMut.isPending || (unresolved === null && !gate.armed)}
        onClick={() => buy()}
      >
        {buyMut.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" /> {unresolved ? 'Settling…' : 'Buying…'}
          </>
        ) : unresolved ? (
          'Settle this purchase'
        ) : (
          gate.label
        )}
      </Button>

      {!gate.armed && gate.blockers.length > 0 && chosen && unresolved === null ? (
        <button
          type="button"
          className="text-center text-xs text-muted-foreground underline"
          onClick={() => setOverrideOpen(true)}
        >
          stuck? manager override — reason required, logged
        </button>
      ) : null}

      {overrideOpen ? (
        <OverrideDialog
          blockers={gate.blockers}
          onClose={() => setOverrideOpen(false)}
          onConfirm={(reason) => {
            setOverrideOpen(false);
            buy(reason);
          }}
        />
      ) : null}
    </aside>
  );
}

function RateRow({
  rate,
  cheapest,
  selected,
  onSelect,
}: {
  rate: ShipmentRate;
  cheapest: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const eta = rateEta(rate);
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md border px-2 py-2 text-left text-sm transition-colors',
        selected ? 'border-emerald-700 bg-emerald-950/40' : 'border-border hover:bg-muted/40',
      )}
    >
      <input
        type="radio"
        name="ship-rate"
        className="sr-only"
        checked={selected}
        onChange={onSelect}
      />
      <span
        aria-hidden="true"
        className={cn(
          'size-3 flex-none rounded-full border',
          selected ? 'border-emerald-400 bg-emerald-400' : 'border-muted-foreground',
        )}
      />
      <span className="min-w-0 flex-1 truncate">
        {rate.carrier} {rate.service}
        {eta ? <span className="text-muted-foreground"> · {eta}</span> : null}
      </span>
      {cheapest ? (
        <span className="flex-none rounded border border-emerald-800 px-1 text-[10px] text-emerald-300">
          CHEAPEST
        </span>
      ) : null}
      <span className="flex-none font-semibold tabular-nums">{formatUsd(rate.rate)}</span>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// After the buy
// ─────────────────────────────────────────────────────────────────────────────

function DoneView({
  manifest,
  shipment,
  onBack,
  refreshOrder,
}: {
  manifest: ShipManifest;
  /** The bought label. Passed in because the CALLER establishes it exists — no cast, no null. */
  shipment: Shipment;
  onBack: () => void;
  refreshOrder: (storeId: string, orderId: string) => void;
}) {
  const client = useApiClient();
  const { storeId, orderId } = manifest;
  const [printing, setPrinting] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const testLabel = isTestLabel(shipment);
  const fulfillment = fulfillmentState(manifest);

  const fulfillMut = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/fulfill', {
        params: { path: { storeId, orderId } },
        body: { notifyCustomer: true },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Shopify did not accept it.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      setError(null);
      refreshOrder(storeId, orderId);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'The write-back failed.'),
  });

  const voidMut = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/shipments/{storeId}/{orderId}/{shipmentId}/void', {
        params: { path: { storeId, orderId, shipmentId: shipment.shipmentId } },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'The void was refused.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      setError(null);
      refreshOrder(storeId, orderId);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'The void failed.'),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="rounded-lg border border-border p-5">
        <h3 className="text-lg font-semibold text-emerald-300">
          Label purchased{shipment.cost != null ? ` · ${formatUsd(shipment.cost)}` : ''}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {shipment.carrier} {shipment.service} · {formatWeight(shipment.parcel.weightOz)} ·{' '}
          {shipment.parcel.lengthIn}×{shipment.parcel.widthIn}×{shipment.parcel.heightIn} in
        </p>

        {testLabel ? (
          <div className="mt-3 rounded-lg border border-amber-800 bg-amber-950/40 p-3 text-sm text-amber-100">
            <b>Test-mode label.</b> Its tracking number is not real: it cannot be pushed to Shopify
            and must not go on a box. Populate the production EasyPost key before shipping for real.
          </div>
        ) : null}

        <div className="mt-4 text-xs text-muted-foreground">Tracking</div>
        <div className="font-mono text-lg tracking-wide">{shipment.trackingCode}</div>

        <div className="mt-4 space-y-1 text-sm">
          {fulfillment === 'created' ? (
            <div className="text-emerald-300">
              ✓ Fulfillment written to Shopify — the customer was emailed the tracking.
            </div>
          ) : fulfillment === 'pending' ? (
            // A marker with no ids = the write-back was ATTEMPTED and did not confirm (the server
            // keeps its claim on an ambiguous failure). Reporting "done" here would tell the operator
            // the customer has tracking when Shopify may never have been told.
            <div className="text-amber-300">
              A push to Shopify was started and did not confirm. The label is bought and safe —
              press again to settle it: if Shopify already fulfilled the order, that reports it
              without sending anything twice.
            </div>
          ) : (
            <div className="text-muted-foreground">
              Not yet pushed to Shopify. The label is bought and safe; this step is separate and can
              be retried without ever re-buying postage.
            </div>
          )}
        </div>

        {error ? (
          <div className="mt-3 rounded-lg border border-red-800 bg-red-950/40 p-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPrinting(true)} disabled={!shipment.labelUrl}>
            <Printer className="size-4" /> Print 4×6
          </Button>
          {fulfillment !== 'created' ? (
            <Button
              onClick={() => fulfillMut.mutate()}
              disabled={fulfillMut.isPending || testLabel}
              title={testLabel ? 'A test-mode label can never be pushed to Shopify.' : undefined}
            >
              {fulfillMut.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Pushing…
                </>
              ) : fulfillment === 'pending' ? (
                'Retry the Shopify push'
              ) : (
                'Push tracking to Shopify'
              )}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => setVoidOpen(true)} disabled={voidMut.isPending}>
            Void label
          </Button>
          <Button className="ml-auto" onClick={onBack}>
            Next order →
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Reprinting is free — it re-prints the label already bought, never a second purchase. Void
          = a refund request to the carrier: UPS voids are near-instant, USPS refunds land as wallet
          credit in days to weeks.
        </p>
      </div>

      <aside className="rounded-lg border border-border p-4">
        <h3 className="mb-2 text-sm font-semibold">What happened</h3>
        <ul className="space-y-2 text-xs text-muted-foreground">
          <li>
            <b className="text-foreground">Verified</b> — {totalVerified(manifest.lines)} of{' '}
            {totalOwed(manifest.lines)} garments
            {shipment.verification?.override ? (
              <span className="text-amber-300">
                {' '}
                · overridden: {shipment.verification.override.reason}
              </span>
            ) : null}
          </li>
          <li>
            <b className="text-foreground">Bought</b> — {shipment.boughtAt?.slice(11, 16)} by{' '}
            {shipment.boughtBy ?? 'the station'}
          </li>
          <li>
            <b className="text-foreground">Provider</b> — {shipment.provider} ·{' '}
            <span className="font-mono">{shipment.providerShipmentId.slice(0, 12)}…</span> ·{' '}
            {shipment.buyAttemptStatus}
          </li>
          {manifest.fulfillment ? (
            <li className={fulfillment === 'pending' ? 'text-amber-300' : undefined}>
              <b className="text-foreground">Shopify</b> —{' '}
              {fulfillment === 'created'
                ? `fulfillment created, ${manifest.fulfillment.trackingCodes.length} tracking code(s)`
                : 'push attempted, outcome unconfirmed'}
            </li>
          ) : null}
          {shipment.refundStatus ? (
            <li className="text-amber-300">
              <b>Refund</b> — {shipment.refundStatus}
            </li>
          ) : null}
        </ul>
      </aside>

      {printing && shipment.labelUrl ? (
        <ShipLabelPrint
          labelUrl={shipment.labelUrl}
          labelFileType={shipment.labelFileType}
          orderName={manifest.orderName}
          trackingCode={shipment.trackingCode ?? ''}
          onClose={() => setPrinting(false)}
        />
      ) : null}

      {voidOpen ? (
        <VoidDialog
          shipment={shipment}
          onClose={() => setVoidOpen(false)}
          onConfirm={() => {
            setVoidOpen(false);
            voidMut.mutate();
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialogs
// ─────────────────────────────────────────────────────────────────────────────

function ManualCodeDialog({
  inputRef,
  onClose,
  onSubmit,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onClose: () => void;
  onSubmit: (code: string) => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Type a garment code</DialogTitle>
          <DialogDescription>
            For a torn or unreadable tag. Scanner capture is paused while this is open.
          </DialogDescription>
        </DialogHeader>
        <Input
          ref={inputRef}
          name="manualCode"
          autoFocus
          placeholder="v1|store|order|line|rank"
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit(e.currentTarget.value.trim());
          }}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(inputRef.current?.value.trim() ?? '')}>Count it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EyeCheckDialog({
  line,
  onClose,
  onConfirm,
}: {
  line: ShipManifestLine;
  onClose: () => void;
  onConfirm: (count: number, reason: string) => void;
}) {
  const remaining = Math.max(0, line.owed - line.scanned);
  const [count, setCount] = useState(remaining);
  const [reason, setReason] = useState('tagless reissue');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Eye-check · {line.title}</DialogTitle>
          <DialogDescription>
            For garments with no scannable tag — a reissued reprint prints none, and Ship-only stock
            never had one. The count is attributed to you and logged against the line.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Label htmlFor="eye-count" className="w-24 flex-none text-sm">
            Counted
          </Label>
          <Input
            id="eye-count"
            name="eyeCount"
            type="number"
            min={0}
            max={line.owed}
            value={count}
            onChange={(e) => setCount(Math.max(0, Math.min(line.owed, Number(e.target.value))))}
          />
          <span className="flex-none text-sm text-muted-foreground">of {line.owed}</span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="eye-reason" className="w-24 flex-none text-sm">
            Why
          </Label>
          <Input
            id="eye-reason"
            name="eyeReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          This replaces any earlier eye-check on the line — enter the true total, not an increment.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(count, reason.trim())}>Record eye-check</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideDialog({
  blockers,
  onClose,
  onConfirm,
}: {
  blockers: string[];
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const ok = reason.trim().length >= 3;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Manager override</DialogTitle>
          <DialogDescription>
            Buy the label past the gate. The reason is recorded on the shipment and in the order’s
            history, next to who did it.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1 rounded-lg border border-amber-800 bg-amber-950/30 p-3 text-sm text-amber-100">
          {blockers.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          <Label htmlFor="override-reason" className="w-16 flex-none text-sm">
            Reason
          </Label>
          <Input
            id="override-reason"
            name="overrideReason"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer collecting at the counter"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ok} onClick={() => onConfirm(reason.trim())}>
            Override and buy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidDialog({
  shipment,
  onClose,
  onConfirm,
}: {
  shipment: Shipment;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Void this label?</DialogTitle>
          <DialogDescription>
            Requests a refund from the carrier for{' '}
            {shipment.cost != null ? formatUsd(shipment.cost) : 'this label'} (
            {shipment.trackingCode}). UPS voids are near-instant; a USPS refund lands as wallet
            credit in days to weeks. The order returns to the queue.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-amber-200">
          Do not void a label that is already on a box in the outbound pile.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Void label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
